// Project API functions
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';
import { resolveStoredFileUrl, toCdnCacheVersion, type UploadFilePayload } from './storage';
import { generateAfter } from './fractionalIndex';

// ============================================================================
// INTERFACES
// ============================================================================

// API 응답의 프로젝트 정보
export interface ApiProject {
  accountCode: string;
  code: string;
  status: string;
  name: string;
  logo: string;
  description: string;
  /** STANDALONE | PLATFORM | BRAND — API 필드 `type` 정규화 결과 */
  projectType?: string;
  parentProjectCode?: string;
  /** 대표 파일 버전 (CDN `?v=` 우선 사용, API Long / DB bigint) */
  primaryVersion?: number | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export type ProjectTypeCode = 'STANDALONE' | 'PLATFORM' | 'BRAND';

export function parseApiProjectType(raw: string | undefined | null): ProjectTypeCode {
  const u = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  if (u === 'PLATFORM' || u === 'BRAND') return u;
  return 'STANDALONE';
}

export function getProjectTypeBracketLabel(type: ProjectTypeCode): string {
  switch (type) {
    case 'PLATFORM':
      return '[플랫폼]';
    case 'BRAND':
      return '[브랜드]';
    default:
      return '[단독]';
  }
}

/** 목록·사이드바 등 프로젝트명 표시용 (타입 미상이면 단독) */
export function formatProjectNameWithType(name: string, projectTypeRaw?: string | null): string {
  const code = parseApiProjectType(projectTypeRaw);
  return `${getProjectTypeBracketLabel(code)} ${name}`;
}

/** API 응답 `type` / `parentCode` → 내부 필드로 정규화 */
export function normalizeApiProject(raw: Record<string, unknown>): ApiProject {
  const typeRaw =
    (typeof raw.type === 'string' ? raw.type : undefined) ??
    (typeof raw.projectType === 'string' ? raw.projectType : undefined);
  const parentRaw =
    (typeof raw.parentCode === 'string' ? raw.parentCode : undefined) ??
    (typeof raw.parentProjectCode === 'string' ? raw.parentProjectCode : undefined);

  const primaryVersionRaw = raw.primaryVersion;
  const primaryVersion =
    typeof primaryVersionRaw === 'number' && Number.isFinite(primaryVersionRaw)
      ? primaryVersionRaw
      : primaryVersionRaw === null
        ? null
        : undefined;

  return {
    accountCode: String(raw.accountCode ?? ''),
    code: String(raw.code ?? ''),
    status: String(raw.status ?? ''),
    name: String(raw.name ?? ''),
    logo: String(raw.logo ?? ''),
    description: String(raw.description ?? ''),
    projectType: typeRaw,
    parentProjectCode: parentRaw,
    primaryVersion,
    createdBy: String(raw.createdBy ?? ''),
    createdAt: String(raw.createdAt ?? ''),
    updatedBy: String(raw.updatedBy ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
  };
}

// API 응답 구조
export interface ApiProjectListResponse {
  timestamp: number;
  errors?: {
    error: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    totalCount: number;
    projects: ApiProject[];
  };
}

// 프로젝트 목록 조회 응답
export interface ProjectListResponse {
  totalCount: number;
  projects: ApiProject[];
}

export interface ProjectListParams {
  keyword: string;
  status: string;
  type: string;
}

export interface ProjectCreateRequest {
  name: string;
  description: string;
  type: ProjectTypeCode;
  parentCode?: string;
}

export interface ProjectUpdateRequest {
  name: string;
  description: string;
  status: string;
  type: ProjectTypeCode;
  parentCode?: string;
}

export interface ProjectFileCreateRequest {
  type: string;
  sortOrder: string;
  tempKey: string;
  tempFileName: string;
  originalName: string;
  contentType: string;
  declaredFileSize: number;
}

interface ApiResponseWrapper<T> {
  timestamp: number;
  errors?: {
    error: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: T;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const getProjectApiHeaders = (): Record<string, string> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
};

const handleProjectApiError = (error: unknown): never => {
  console.error('프로젝트 API 오류:', error);

  if (CommonUtils.isNetworkError(error)) {
    const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
    (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
    throw networkError;
  }

  throw error;
};

/** 프로젝트 이름 중복 확인 */
export const existsProjectName = async (name: string): Promise<boolean> => {
  try {
    const response = await fetch(
      `${getApiV1Url('/projects/exists-name')}?name=${encodeURIComponent(name)}`,
      {
        method: 'GET',
        headers: getProjectApiHeaders(),
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result: ApiResponseWrapper<{ exists: boolean }> = await response.json();
    console.log('프로젝트 이름 중복 확인 응답:', result);
    return result.data?.exists ?? false;
  } catch (error) {
    return handleProjectApiError(error);
  }
};

/** 프로젝트 상세 조회 */
export const getProjectDetail = async (projectCode: string): Promise<ApiProject> => {
  try {
    const response = await fetch(getApiV1Url(`/projects/${projectCode}`), {
      method: 'GET',
      headers: getProjectApiHeaders(),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result: ApiResponseWrapper<Record<string, unknown>> = await response.json();
    console.log('프로젝트 상세 API 응답:', result);

    if (!result.data) {
      throw new Error('프로젝트 정보를 찾을 수 없습니다.');
    }

    return normalizeApiProject(result.data);
  } catch (error) {
    return handleProjectApiError(error);
  }
};

/** 프로젝트 생성 */
export const createProject = async (request: ProjectCreateRequest): Promise<ApiProject> => {
  try {
    const body: ProjectCreateRequest = {
      name: request.name,
      description: request.description,
      type: request.type,
    };
    if (request.type === 'BRAND' && request.parentCode?.trim()) {
      body.parentCode = request.parentCode.trim();
    }

    console.log('[프로젝트 생성] API 요청', { url: getApiV1Url('/projects'), body });

    const response = await fetch(getApiV1Url('/projects'), {
      method: 'POST',
      headers: getProjectApiHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result: ApiResponseWrapper<Record<string, unknown>> = await response.json();
    console.log('프로젝트 생성 성공 - 서버 응답:', result);

    if (!result.data) {
      throw new Error('프로젝트 생성 응답이 올바르지 않습니다.');
    }

    return normalizeApiProject(result.data);
  } catch (error) {
    return handleProjectApiError(error);
  }
};

/** 프로젝트 수정 */
export const updateProject = async (
  projectCode: string,
  request: ProjectUpdateRequest
): Promise<ApiProject> => {
  try {
    const body: ProjectUpdateRequest = {
      name: request.name,
      description: request.description,
      status: request.status,
      type: request.type,
    };
    if (request.type === 'BRAND' && request.parentCode?.trim()) {
      body.parentCode = request.parentCode.trim();
    }

    console.log('[프로젝트 수정] API 요청', {
      url: getApiV1Url(`/projects/${projectCode}`),
      body,
    });

    const response = await fetch(getApiV1Url(`/projects/${projectCode}`), {
      method: 'POST',
      headers: getProjectApiHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result: ApiResponseWrapper<Record<string, unknown>> = await response.json();
    console.log('프로젝트 수정 성공 - 서버 응답:', result);

    if (!result.data) {
      throw new Error('프로젝트 수정 응답이 올바르지 않습니다.');
    }

    return normalizeApiProject(result.data);
  } catch (error) {
    return handleProjectApiError(error);
  }
};

/** uploadFiles payload → 프로젝트 파일(대표 파일) 등록 요청 */
export const toProjectPrimaryFileRequest = (
  uploadFile: UploadFilePayload
): ProjectFileCreateRequest => ({
  type: 'PRIMARY',
  sortOrder: generateAfter(null),
  tempKey: uploadFile.tempKey,
  tempFileName: uploadFile.tempFileName,
  originalName: uploadFile.originalName,
  contentType: uploadFile.contentType,
  declaredFileSize: uploadFile.declaredFileSize,
});

/** 프로젝트 로고 스토리지 URI: files/{projectCode}/project/logo.webp */
export const getProjectPrimaryStorageUri = (projectCode: string): string =>
  `/files/${projectCode}/project/logo.webp`;

/** 프로젝트 로고 썸네일 스토리지 URI: files/{projectCode}/project/thumb/logo.webp */
export const getProjectPrimaryThumbnailStorageUri = (projectCode: string): string =>
  `/files/${projectCode}/project/thumb/logo.webp`;

/** 프로젝트 대표 파일 표시 URL (CDN 설정 시 CDN+?v= / 없으면 presign) */
export const resolveProjectPrimaryUrl = async (
  projectCode: string,
  primaryVersion?: number | null,
  updatedAt?: string | number | null
): Promise<string | null> =>
  resolveStoredFileUrl(getProjectPrimaryThumbnailStorageUri(projectCode), {
    cacheVersion: toCdnCacheVersion(primaryVersion, updatedAt),
  });

/** 프로젝트 파일 등록 */
export const createProjectFile = async (
  projectCode: string,
  request: ProjectFileCreateRequest
): Promise<void> => {
  try {
    console.log('[프로젝트 파일 등록] API 요청', {
      url: getApiV1Url(`/projects/${projectCode}/files`),
      body: request,
    });

    const response = await fetch(getApiV1Url(`/projects/${projectCode}/files`), {
      method: 'POST',
      headers: getProjectApiHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('프로젝트 파일 등록 성공 - 서버 응답:', result);
  } catch (error) {
    return handleProjectApiError(error);
  }
};

/** 프로젝트 대표 파일 삭제 */
export const deleteProjectPrimary = async (projectCode: string): Promise<void> => {
  try {
    const url = getApiV1Url(`/projects/${projectCode}/files/primary/delete`);
    console.log('[프로젝트 대표 파일 삭제] API 요청', { url, method: 'POST' });

    const response = await fetch(url, {
      method: 'POST',
      headers: getProjectApiHeaders(),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('프로젝트 대표 파일 삭제 성공 - 서버 응답:', result);
  } catch (error) {
    return handleProjectApiError(error);
  }
};

// ============================================================================
// API FUNCTIONS (목록)
// ============================================================================

// 프로젝트 목록 조회 API
export const getProjectList = async (
  params: Partial<ProjectListParams> = {}
): Promise<ProjectListResponse> => {
  try {
    const headers = getProjectApiHeaders();

    const query = new URLSearchParams();
    if (params.keyword) query.set('keyword', params.keyword);
    if (params.status) query.set('status', params.status);
    if (params.type) query.set('type', params.type);

    const url = `${getApiV1Url('/projects')}${query.toString() ? `?${query.toString()}` : ''}`;

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result: ApiProjectListResponse = await response.json();
    console.log('프로젝트 목록 API 응답:', result);
    
    if (result.data && result.data.projects) {
      return {
        totalCount: result.data.totalCount || 0,
        projects: result.data.projects.map((p) =>
          normalizeApiProject(p as unknown as Record<string, unknown>)
        ),
      };
    }
    
    return {
      totalCount: 0,
      projects: []
    };
  } catch (error: any) {
    console.error('프로젝트 목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};